import React from 'react'
import { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import RichTextType from '@/models/contentful/richText-models'
import { renderEmbeddedAssets } from './renderAssets'
import { RichTextAssetType, RichTextEntry } from './types'
import { Heading, Paragraph } from '../typography'
import { renderEmbeddedEntryBlock } from './renderEntries'
import TableRenderer from './table-renderer/TableRenderer'
import { RenderOptions } from './renderOptions'
import { getRichTextItemsMap } from './getRichTextItemsMap'

const getRenderOptions = (
  document: RichTextType,
  options: RenderOptions = {
    decorateImages: true,
    skipParagraphWrapper: false,
  },
): Options => {
  const items = document.links
    ? getRichTextItemsMap(document.links)
    : {
        assets: new Map(),
        entries: new Map(),
      }

  return {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    },
    renderNode: {
      // Table renderers
      [BLOCKS.TABLE]: (node, children) => <TableRenderer>{children}</TableRenderer>,
      // Unordered list
      [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
      // Ordered list
      [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
      // List item
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,

      // Links
      [INLINES.HYPERLINK]: (node, children) => {
        const { uri } = node.data
        return (
          <a style={{ fontWeight: 600 }} href={uri} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const sysId = node?.data?.target?.sys?.id
        if (!sysId) return <></>

        const asset = items.assets.get(sysId) as RichTextAssetType
        if (!asset) return <></>

        return renderEmbeddedAssets(asset, options)
      },
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (options.skipParagraphWrapper) {
          return <>{children}</>
        }
        return <p>{children}</p>
      },

      [BLOCKS.HEADING_3]: (node, children) => (
        <Heading variant="semi-bold-26" as="h3" responsive={{ maxSize: 26, minSize: 22 }}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <Heading variant="semi-bold-22" as="h4" responsive={{ maxSize: 22, minSize: 22 }}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <Paragraph variant="regular-18" as="h5" responsive={{ maxSize: 18, minSize: 18 }}>
          {children}
        </Paragraph>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <Paragraph variant="regular-14" as="h6" color="#B0B0B0" responsive={{ maxSize: 14, minSize: 14 }}>
          {children}
        </Paragraph>
      ),

      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const sysId = node?.data?.target?.sys?.id
        if (!sysId) return null

        const entry = items.entries.get(sysId) as RichTextEntry

        if (!entry) return null

        return renderEmbeddedEntryBlock(entry)
      },
    },

    // preserveWhitespace: true,
  }
}

export default getRenderOptions
